// Universal javascript — main entry

// Tailwind Plus web components — registers <el-disclosure>, <el-dialog>,
// etc. as custom elements. The header search popover, mobile menu, and
// FAQ accordion all rely on these. (faqs/centered-accordion.twig also
// loads it from CDN as a fallback — that can go once we're sure the
// bundled version is working in all contexts.)
import '@tailwindplus/elements';

// Utilities
import disableTransitionOnResize from './utils/disableTransitionOnResize.js';
import refreshCsrfToken from './utils/refresh-csrf-token.js';
import newsletterSubscribe from './utils/newsletter-subscribe.js';

// Accept HMR
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log('HMR');
    });
}


document.addEventListener('DOMContentLoaded', function() {
    // Suppress CSS transitions while the viewport is being resized.
    disableTransitionOnResize.init();

    // Refresh the CSRF token from Craft (required for forms on Blitz-cached
    // pages — embedded tokens go stale). Populates window.__csrfToken once
    // the fetch resolves so consumers can read it synchronously.
    refreshCsrfToken.init().then(() => {
        window.__csrfToken = refreshCsrfToken.getToken();
        // Patch the hidden CSRF inputs in any newsletter form on the page.
        // refresh-csrf-token already updates inputs with the env-configured
        // token name; nothing extra needed here, but the AJAX submit picks
        // up whatever the input holds at submit time.
    });
    document.addEventListener('csrf:refreshed', (e) => {
        window.__csrfToken = e.detail;
    });

    // AJAX wiring for all newsletter signup forms on the page (popup +
    // content-builder block both share _partials/newsletter-form.twig).
    newsletterSubscribe.init();

    // Get the button and container elements
    const backToTopButton = document.getElementById('scroll-to-top');
    const backToTopContainer = document.getElementById('scroll-to-top-container');

    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Function to toggle button visibility based on scroll position
    function toggleBackToTopButton() {
        if (window.scrollY > 300) {
            backToTopContainer.classList.add('visible');
            backToTopContainer.classList.remove('hidden');
        } else {
            backToTopContainer.classList.remove('visible');
            backToTopContainer.classList.add('hidden');
        }
    }

    // Function to scroll to top smoothly
    function scrollToTop() {
        try {
            if (prefersReducedMotion.matches) {
                // If user prefers reduced motion, use instant scrolling
                window.scrollTo({
                    top: 0,
                    behavior: 'auto'
                });
            } else {
                // Use smooth scrolling for users who don't prefer reduced motion
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }

            // Focus management for accessibility - return focus to top of page
            // First, check if there's a skip link or the target is focusable
            const target = document.querySelector('#top');
            if (target) {
                target.setAttribute('tabindex', '-1');
                target.focus();

                // Remove tabindex after focusing to avoid it being tabbable
                setTimeout(() => {
                    target.removeAttribute('tabindex');
                }, 1000);
            }
        } catch (error) {
            // Fallback for older browsers
            window.scrollTo(0, 0);
        }
    }

    // Show/hide button on scroll
    window.addEventListener('scroll', toggleBackToTopButton);

    // Scroll to top when button is clicked
    backToTopButton.addEventListener('click', scrollToTop);

    // Also allow activation with keyboard (Enter/Space)
    backToTopButton.addEventListener('keydown', function(event) {
        // Check for Enter or Space key
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            scrollToTop();
        }
    });

    // Initialize button state
    toggleBackToTopButton();

    // ----------------------------------------------------------------
    // Nav dropdowns: ensure only one <details> is open at a time.
    // CSS (in templates/_partials/header.twig) handles the opacity +
    // translate transition while the <details> open state changes.
    // ----------------------------------------------------------------
    const navDropdowns = document.querySelectorAll('.nav-dropdown');
    navDropdowns.forEach((detail) => {
        detail.addEventListener('toggle', () => {
            if (!detail.open) return;
            navDropdowns.forEach((other) => {
                if (other !== detail) other.open = false;
            });
        });
    });

    // ----------------------------------------------------------------
    // Newsletter popup
    // - Auto-opens after data-show-delay seconds if the dismissal
    //   cookie isn't set.
    // - Close button (or ESC) sets the cookie for data-dismiss-days.
    // - [data-newsletter-trigger] re-opens it (e.g. footer link) and
    //   ignores the cookie so the user can opt back in.
    // - Successful submit (form submit event) marks dismissed for a
    //   full year so it stops popping up.
    // ----------------------------------------------------------------
    const newsletterPopup = document.querySelector('[data-newsletter-popup]');
    if (newsletterPopup) {
        const COOKIE_NAME = 'newsletter_dismissed';
        const dismissDays = parseInt(newsletterPopup.dataset.dismissDays || '30', 10);
        const showDelayMs = parseInt(newsletterPopup.dataset.showDelay || '5', 10) * 1000;

        const getCookie = (name) => {
            const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
            return m ? decodeURIComponent(m[1]) : null;
        };
        const setCookie = (name, value, days) => {
            const expires = new Date(Date.now() + days * 86400000).toUTCString();
            document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
        };

        const openPopup = () => {
            if (typeof newsletterPopup.showModal === 'function') {
                newsletterPopup.showModal();
            } else {
                newsletterPopup.setAttribute('open', '');
            }
        };
        const closePopup = () => {
            if (typeof newsletterPopup.close === 'function') {
                newsletterPopup.close();
            } else {
                newsletterPopup.removeAttribute('open');
            }
        };
        const dismiss = () => {
            setCookie(COOKIE_NAME, '1', dismissDays);
            closePopup();
        };

        // Close button
        const closeBtn = newsletterPopup.querySelector('[data-newsletter-close]');
        if (closeBtn) closeBtn.addEventListener('click', dismiss);

        // ESC / backdrop close (native <dialog> "close" event)
        newsletterPopup.addEventListener('close', () => {
            // Only set the cookie if it wasn't already (avoid overwriting submit cookie).
            if (!getCookie(COOKIE_NAME)) {
                setCookie(COOKIE_NAME, '1', dismissDays);
            }
        });

        // Manual triggers (e.g. footer link). Ignore the cookie.
        document.querySelectorAll('[data-newsletter-trigger]').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                openPopup();
            });
        });

        // (Form submit success → "subscribed" cookie + dialog close are now
        // handled inside newsletter-subscribe.js, conditional on the Mailchimp
        // API call actually succeeding. The popup just owns auto-open and
        // dismissal here.)

        // Auto-open if not previously dismissed.
        if (!getCookie(COOKIE_NAME)) {
            setTimeout(openPopup, showDelayMs);
        }
    }
});
